import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import DomainList from './DomainList';
import ProductGrid from './ProductGrid';
import MobileMegaMenu from './MobileMegaMenu';
import { ArrowRight } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useLanguage } from '../../../context';
import { getLocalizedPath } from '../../../helpers/routes';

const MegaMenu = ({ category, onClose, onDomainChange }) => {
  const [activeDomain, setActiveDomain] = useState(null);
  const location = useLocation();
  const intl = useIntl();
  const { language } = useLanguage();

  const getItemUrl = (item) => {
    const fallbackUrl = getLocalizedPath(
      `/info/${category?.slugFr || category?.slug}/${item.slug}`,
      language
    );
    return item?.link || fallbackUrl;
  };

  // Domaine correspondant à la page actuellement visitée
  const activeDomainFromPath = useMemo(() => {
    if (!category?.submenus || category.submenus.length === 0) return null;
    const match = category.submenus.find((item) => getItemUrl(item) === location.pathname);
    return match ? match.parentDomain : null;
  }, [category, location.pathname, language]);

  const isCompanies =
    (category?.slug || '').toLowerCase() === 'companies' ||
    (category?.title || '').toLowerCase() === 'companies' ||
    (category?.title || '').toLowerCase() === 'about us';

  // Préparer les données avec useMemo pour éviter de recalculer à chaque rendu
  const domainProductMap = useMemo(() => {
    const map = {};
    
    category.submenus.forEach(item => {
      if (!map[item.parentDomain]) {
        map[item.parentDomain] = [];
      }
      map[item.parentDomain].push(item);
    });
    
    return map;
  }, [category.submenus]);

  // Définir le premier domaine comme actif par défaut
  const firstDomain = useMemo(() => {
    if (Object.keys(domainProductMap).length > 0) {
      return Object.keys(domainProductMap)[0];
    }
    return null;
  }, [domainProductMap]);

  // Si aucun domaine actif n'est défini, utiliser celui de la page courante, sinon le premier
  const currentActiveDomain = activeDomain || activeDomainFromPath || firstDomain;

  const handleDomainChange = (domain) => {
    setActiveDomain(domain);
    if (onDomainChange) {
      onDomainChange(domain);
    }
  };

  // Version desktop
  return (
    <div className="hidden lg:block w-full">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <div className="flex">
          {isCompanies ? (
            <ProductGrid
              showAll
              title={category?.title}
              products={category?.submenus || []}
              category={category}
              onItemClick={onClose}
            />
          ) : (
            <>
              {/* Colonne gauche - Liste des domaines */}
              <DomainList 
                domains={Object.keys(domainProductMap)} 
                activeDomain={currentActiveDomain}
                onDomainSelect={handleDomainChange}
              />
              
              {/* Zone principale - Produits du domaine sélectionné */}
              <ProductGrid 
                domain={currentActiveDomain}
                products={domainProductMap[currentActiveDomain] || []}
                category={category}
                onItemClick={onClose}
              />
            </>
          )}
        </div>
        {/* Bottom Action Bar */}
        <div className="border-t border-slate-200 mt-8 pt-6 flex justify-between items-center">
          <div className="flex space-x-6">
            <a
              href="#"
              className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:text-[#16A34A] transition-colors"
            >
              {intl.formatMessage({ id: "menu.advancedSearch" })}
            </a>
            <a
              href="#"
              className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:text-[#16A34A] transition-colors"
            >
              {intl.formatMessage({ id: "menu.downloadCatalog" })}
            </a>
          </div>
          <a
            href={getLocalizedPath("/contact", language)}
            className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200"
          >
            <span>{intl.formatMessage({ id: "menu.contactSales" })}</span>
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;


